import React, { useState, useEffect } from 'react';
import { CustomInput, ToggleButton } from '../../../components/common';
import { Client, Package, useUpdateClient, useUpdatePackage } from '../../../hooks/useClients';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface EligibilityConfigurationProps {
  packageData: Package;
  defaultPackage: number;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const EligibilityConfiguration: React.FunctionComponent<EligibilityConfigurationProps> = ({
  packageData,
  defaultPackage,
  setSaveDataModal,
}): JSX.Element => {
  const { updatePackage } = useUpdatePackage(packageData.client_id!, packageData.id!, true);
  const { updateClient } = useUpdateClient(packageData.client_id!, true);

  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [domainList, setDomainList] = useState('');
  const [isDefaultPackage, setIsDefaultPackage] = useState(false);
  const queryClient = useQueryClient();

  const handleSave = async () => {
    const payload: Partial<Package> = {
      domain_whitelist: domainList,
    };
    const clientPayload = {
      default_package: packageData.id,
    };
    // We only want to update the client if we're changing the default package;
    // We'll still update the package, so we need to bypass the existing onSuccess handlers
    // for both the package and the client, in an effort to only show one toast notification.
    if (isDefaultPackage && packageData.id !== defaultPackage) {
      try {
        await updatePackage.mutateAsync(payload, {
          onSuccess: () => {
            updateClient.mutateAsync(clientPayload as Partial<Client>, {
              onSuccess: () => {
                queryClient.invalidateQueries(['clients', packageData.client_id]);
                toast.success('Package updated successfully');
              },
              onError: (error) => {
                console.error('Error updating client:', error);
                toast.error('Failed to update package');
              },
            });
          },
          onError: (error) => {
            console.error('Error updating package:', error);
            toast.error('Failed to update package');
          },
        });
      } catch (error) {
        console.error('Error in update sequence:', error);
        toast.error('Failed to update package');
      }
    } else {
      updatePackage.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(['clients', packageData.client_id]);
          toast.success('Package updated successfully');
        },
      });
    }
  };

  useEffect(() => {
    if (packageData) {
      setDomainList(packageData?.domain_whitelist || '');
      setIsDefaultPackage(!!(defaultPackage === packageData.id));
      setEnableSaveOption(false);
    }
  }, [packageData, defaultPackage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption]);

  return (
    <>
      <div className="gray-border-line flex w-full justify-between border-b py-3">
        <div className="text-md text-gray-dark font-bold uppercase">Eligibility Configuration</div>
        <button
          className={
            'h-6 rounded-full px-2 text-center text-sm text-white ' +
            (enableSaveOption ? ' stacked-bar-blue-400' : ' bg-gray-background-light cursor-not-allowed')
          }
          onClick={handleSave}
        >
          {' '}
          SAVE{' '}
        </button>
      </div>
      <div className={`mb-5 grid grid-cols-2 gap-4 py-2.5`}>
        <div className="col-span-1">
          <CustomInput
            inputType={'text'}
            value={domainList}
            setValue={(data) => {
              setEnableSaveOption(true);
              setDomainList(data);
            }}
            Label={'Allowed Email Domains'}
            dataTestId="allowed-email-domains"
          />
        </div>
        <div className="col-span-1 flex">
          <div className="text-dark-blue mr-2 flex items-center text-sm font-medium leading-4">Default Package</div>
          <div className="flex items-center px-3">
            <ToggleButton
              status={isDefaultPackage}
              setStatus={(data) => {
                setEnableSaveOption(true);
                setIsDefaultPackage(data);
              }}
              disabled={!!(defaultPackage === packageData.id)}
              id={'default-package'}
            />
          </div>
        </div>
      </div>
      <div className="gray-border-line w-full border-b"></div>
    </>
  );
};

export default EligibilityConfiguration;
